import { useMemo } from 'react';

import type { Capabilities } from '@rtkwlf/rendall';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useLegacyCapabilities } from '@apis/rendall/queries/use-capabilities';
import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';
import { DurationValue } from '@packages/utils/durationSelector/durationSelector.utils';
import { EVENT_LOG_SEARCH_ROUTE } from 'ae-constants/routes.constants';

import { DatePickerShortcuts } from '../use-date-picker-shortcuts/use-date-picker-shortcuts';

const getDefaultDateRange = (): [start: Dayjs, end: Dayjs] => {
  const today = dayjs();

  return [today.subtract(1, 'week'), today];
};

const getDefaultLiteDateRange = (): [start: Dayjs, end: Dayjs] => {
  const today = dayjs();

  return [today.subtract(3, 'days'), today];
};

export interface DataExplorerConfiguration {
  title: string;
  eventLogResultsTranslationKey: string;
  datePicker: {
    maxRange: number;
    menuOptions: DatePickerShortcuts[];
    getDefaultDateRange: () => [start: Dayjs, end: Dayjs];
  };
  durationSelector: {
    options?: DurationValue[];
    maxDayLimit?: number;
  };
  menuItem: {
    text: string;
    to: string;
    legacyCapabilities?: Partial<Capabilities>;
  };
  canViewDataVisualization: boolean;
  canViewPresetFieldSearch: boolean;
}

function useDataExplorerConfigurations() {
  const { t } = useTranslation();

  return useMemo<{
    full: DataExplorerConfiguration;
    lite: DataExplorerConfiguration;
  }>(
    () => ({
      full: {
        title: t('navMenu:dataExplorer'),
        eventLogResultsTranslationKey:
          'eventLogSearch:showingAnalyzedResults.full',
        datePicker: {
          maxRange: 10,
          menuOptions: [
            DatePickerShortcuts.PastHour,
            DatePickerShortcuts.Past24,
            DatePickerShortcuts.Past72,
            DatePickerShortcuts.PastWeek,
          ],
          getDefaultDateRange,
        },
        durationSelector: {
          options: [
            DurationValue.OneHour,
            DurationValue.TwelveHour,
            DurationValue.TwentyFourHour,
            DurationValue.SeventyTwoHour,
            DurationValue.SevenDay,
            DurationValue.TenDay,
          ],
          maxDayLimit: 10,
        },
        menuItem: {
          legacyCapabilities: { canAccessAnalyzedData: true },
          text: t('navMenu:dataExplorer'),
          to: EVENT_LOG_SEARCH_ROUTE,
        },
        canViewDataVisualization: false,
        canViewPresetFieldSearch: false,
      },
      lite: {
        title: t('navMenu:dataExplorerLite'),
        eventLogResultsTranslationKey:
          'eventLogSearch:showingAnalyzedResults.lite',
        datePicker: {
          maxRange: 3,
          menuOptions: [
            DatePickerShortcuts.PastHour,
            DatePickerShortcuts.Past24,
            DatePickerShortcuts.Past72,
          ],
          getDefaultDateRange: getDefaultLiteDateRange,
        },
        durationSelector: {
          options: [
            DurationValue.OneHour,
            DurationValue.TwelveHour,
            DurationValue.TwentyFourHour,
            DurationValue.SeventyTwoHour,
          ],
          maxDayLimit: 3,
        },
        menuItem: {
          legacyCapabilities: { canAccessAnalyzedDataLite: true },
          text: t('navMenu:dataExplorerLite'),
          to: EVENT_LOG_SEARCH_ROUTE,
        },
        canViewDataVisualization: false,
        canViewPresetFieldSearch: false,
      },
    }),
    [t],
  );
}

export const useDataExplorerConfiguration = (): {
  dataExplorerConfiguration: DataExplorerConfiguration;
  isFetching: boolean;
} => {
  const [dataVizFF] = useFeatureFlag('DATA_EXPLORER_VISUALIZATION');
  const { data: legacyCapabilities, isFetching } = useLegacyCapabilities();
  const dataExplorerConfigurations = useDataExplorerConfigurations();

  const dataExplorerConfiguration = useMemo(() => {
    if (legacyCapabilities?.canAccessAnalyzedData) {
      if (dataVizFF) {
        return {
          ...dataExplorerConfigurations.full,
          canViewDataVisualization: true,
          canViewPresetFieldSearch: true,
        };
      }
      return dataExplorerConfigurations.full;
    }

    if (legacyCapabilities?.canAccessAnalyzedDataLite) {
      return dataExplorerConfigurations.lite;
    }

    return dataExplorerConfigurations.full;
  }, [
    dataExplorerConfigurations.full,
    dataExplorerConfigurations.lite,
    dataVizFF,
    legacyCapabilities?.canAccessAnalyzedData,
    legacyCapabilities?.canAccessAnalyzedDataLite,
  ]);

  return { dataExplorerConfiguration, isFetching };
};
